import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, MapPin, Loader2, Check, Building2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

/**
 * Smart Address Search Component
 * Similar to Gojek/Grab/Tokopedia address picker
 * Uses Komerce API for Indonesian address lookup
 */
export default function AddressSearch({ 
  value = {}, 
  onChange,
  disabled = false,
  className = ''
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(value || null);
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize with existing value
  useEffect(() => {
    if (value && value.destination_id) {
      setSelectedLocation(value);
    }
  }, [value]);

  // Debounced search
  const handleSearchChange = (query) => {
    setSearchQuery(query);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    setShowDropdown(true);

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/shipping/destinations/search`, {
          params: { keyword: query }
        });
        
        if (response.data.success && response.data.destinations) {
          setSearchResults(response.data.destinations.slice(0, 10)); // Limit to 10 results
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error('Error searching destinations:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce
  };

  // Handle location selection
  const handleSelectLocation = (destination) => {
    const locationData = {
      destination_id: destination.id,
      subdistrict_name: destination.subdistrict_name,
      district_name: destination.district_name,
      city_name: destination.city_name,
      province_name: destination.province_name,
      postal_code: destination.zip_code,
      full_label: destination.label
    };

    setSelectedLocation(locationData);
    setSearchQuery('');
    setShowDropdown(false);
    setSearchResults([]);

    // Call parent onChange
    if (onChange) {
      onChange(locationData);
    }
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedLocation(null);
    setSearchQuery('');
    if (onChange) {
      onChange(null);
    }
  };

  return (
    <div className={`space-y-4 ${className}`} ref={dropdownRef}>
      {/* Search Input */}
      <div className="relative">
        <Label className="dark:text-zinc-200 flex items-center gap-2 mb-2">
          <Search className="w-4 h-4" />
          Cari Lokasi
        </Label>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 dark:text-zinc-400" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => searchQuery.length >= 2 && setShowDropdown(true)}
            placeholder="Ketik nama kelurahan, kecamatan, atau kota..."
            className="pl-10 pr-10 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
            disabled={disabled}
          />
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500 animate-spin" />
          )}
        </div>

        {/* Dropdown Results */}
        {showDropdown && (searchResults.length > 0 || isSearching) && (
          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {isSearching ? (
              <div className="p-4 text-center text-slate-500 dark:text-zinc-400">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Mencari...
              </div>
            ) : searchResults.length > 0 ? (
              searchResults.map((dest) => (
                <button
                  key={dest.id}
                  type="button"
                  onClick={() => handleSelectLocation(dest)}
                  className="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-zinc-800 border-b border-slate-100 dark:border-zinc-800 last:border-0 transition-colors dark:bg-zinc-900"
                >
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-zinc-50">
                        {dest.subdistrict_name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-zinc-400">
                        {dest.district_name}, {dest.city_name}, {dest.province_name}
                      </p>
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5">
                        Kode Pos: {dest.zip_code}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="p-4 text-center text-slate-500 dark:text-zinc-400">
                Tidak ditemukan. Coba kata kunci lain.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected Location Display */}
      {selectedLocation && selectedLocation.destination_id && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-800 rounded-lg">
                <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-green-800 dark:text-green-200">
                  Lokasi Terpilih
                </p>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  {selectedLocation.subdistrict_name}, {selectedLocation.district_name}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400">
                  {selectedLocation.city_name}, {selectedLocation.province_name} - {selectedLocation.postal_code}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClearSelection}
              className="text-xs text-green-600 dark:text-green-400 hover:underline"
              disabled={disabled}
            >
              Ganti
            </button>
          </div>
        </div>
      )}

      {/* Address Details (Read-only, auto-filled) */}
      {selectedLocation && selectedLocation.destination_id && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-500 dark:text-zinc-400">Provinsi</Label>
            <div className="mt-1 px-3 py-2 bg-slate-100 dark:bg-zinc-800 rounded-lg text-sm text-slate-700 dark:text-zinc-300">
              {selectedLocation.province_name || '-'}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 dark:text-zinc-400">Kota/Kabupaten</Label>
            <div className="mt-1 px-3 py-2 bg-slate-100 dark:bg-zinc-800 rounded-lg text-sm text-slate-700 dark:text-zinc-300">
              {selectedLocation.city_name || '-'}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 dark:text-zinc-400">Kecamatan</Label>
            <div className="mt-1 px-3 py-2 bg-slate-100 dark:bg-zinc-800 rounded-lg text-sm text-slate-700 dark:text-zinc-300">
              {selectedLocation.district_name || '-'}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 dark:text-zinc-400">Kelurahan</Label>
            <div className="mt-1 px-3 py-2 bg-slate-100 dark:bg-zinc-800 rounded-lg text-sm text-slate-700 dark:text-zinc-300">
              {selectedLocation.subdistrict_name || '-'}
            </div>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <Label className="text-xs text-slate-500 dark:text-zinc-400">Kode Pos</Label>
            <div className="mt-1 px-3 py-2 bg-slate-100 dark:bg-zinc-800 rounded-lg text-sm text-slate-700 dark:text-zinc-300 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
              {selectedLocation.postal_code || '-'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
